import React, { useEffect, useState, useCallback, useRef } from "react";
import "./editItem.css";
import {
  editJobItemQty,
  FilterProduct,
  addComplimentaryinKDS,
  revertPrevAction,
  dataRetriveWhenPageRefresh,
} from "../../../../../config/Services";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import { GiftIcon } from "@heroicons/react/20/solid";
import { ArrowPathRoundedSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { CircularProgress, Drawer } from "@mui/material";
import CancellationReview from "./CancellationReview";
import { validateRejectionReason } from "../../rejectionReasons";
import {
  cancellableItems,
  loadCancellationContext,
} from "../../orderCancellation";
const EditItem = ({ order, onClose, onSuccess, saleMode = "DINE_IN" }) => {
  const [items, setItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [changedItems, setChangedItems] = useState([]);
  const [openComplimentary, setOpenComplimentary] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [complimentaryItems, setComplimentaryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCancellationReview, setShowCancellationReview] = useState(false);
  const [cancellationContext, setCancellationContext] = useState(null);
  const [cancellationContextError, setCancellationContextError] = useState("");
  const [isLoadingCancellationContext, setIsLoadingCancellationContext] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [customCancellationReason, setCustomCancellationReason] = useState("");
  const [cancellationError, setCancellationError] = useState("");
  const [markWastage, setMarkWastage] = useState(null);
  const [doNotCharge, setDoNotCharge] = useState(false);
  const cancellingRef = useRef(false);

  // New states for API search
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasManualEdits, setHasManualEdits] = useState(false);
  const [resetLoader, setResetLoader] = useState(false);

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const customerName = JSON.parse(localStorage.getItem("customer_details"))?.name;
  const appConfigData = localStorage.getItem("appConfig");
  const appConfig = appConfigData ? JSON.parse(localStorage.getItem("appConfig")) : {};

  const showSnackbar = useSnackbar();

  // ===== Fetch products from API =====
  const fetchComplimentaryProducts = useCallback(
    async (searchQuery) => {
      if (!searchQuery || searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const payload = {
          page: 1,
          pageSize: 10,
          query: searchQuery,
          storeLocations: [locId],
          storeIds: [orgId],
          productTypes: ["MENU"],
          salesChannels: [saleMode],
          flattenResponse: true,
        };

        const response = await FilterProduct(payload);
        const result = response?.data?.data;

        if (response?.data?.status === "SUCCESS" && result?.data?.data) {
          const products = result?.data?.data || [];
          const formattedProducts = products.map((product) => {
            const salesChannelData = product?.variant?.externalSalesChannels?.find(
              (channel) => channel.salesChannelName === saleMode
            );

            const stockStatus =
              salesChannelData?.stockStatus || product?.variant?.stockStatus || "OUT_OF_STOCK";
            const isInStock = stockStatus === "IN_STOCK";

            return {
              id: `${product?.productId}-${product?.variant?.variantId}-${saleMode}`,
              productId: product?.id,
              variantId: product?.variant?.variantId,
              externalId: product?.variant?.externalId || product?.variant?.variantId,
              name: `${product?.name} ${product?.variant?.name}`,
              ingredients:
                product?.ingredients?.join(", ") || product?.description || "No ingredients listed",
              inStock: isInStock,
              categoryLevel1: product?.categoryLevel1,
              foodType: product?.attributes?.foodType,
              productName: product?.name,
            };
          });

          // Filter out products already in complimentary items
          const filtered = formattedProducts.filter(
            (p) => !complimentaryItems.some((c) => c.externalId === p.externalId)
          );

          setSearchResults(filtered);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [locId, saleMode, complimentaryItems]
  );

  // ===== Debounced search =====
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchComplimentaryProducts(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, fetchComplimentaryProducts]);

  // ===== Decrease quantity or move to remover =====
  const handleDecrease = (id) => {
    setHasManualEdits(true);
    setItems((prevItems) => {
      const index = prevItems.findIndex((i) => i.externalId === id);
      if (index === -1) return prevItems;

      const item = prevItems[index];
      const newQty = Math.max(0, item?.qty - 1);

      // update top list (remove from top if newQty === 0)
      const next = [...prevItems];
      if (newQty === 0) next.splice(index, 1);
      else next[index] = { ...item, qty: newQty };

      // update removed list (qty = current remaining; removedQty = diff)
      setRemovedItems((prevRemoved) => {
        const removedQty = item?.originalQty - newQty;
        const base = { ...item, qty: newQty, removedQty, status: "REMOVED" };
        const i2 = prevRemoved.findIndex((r) => r.externalId === id);
        if (i2 !== -1) {
          const up = [...prevRemoved];
          up[i2] = base;
          return up;
        }
        return [...prevRemoved, base];
      });

      return next;
    });
  };

  const handleIncrease = (id) => {
    setHasManualEdits(true);
    setItems((prev) => {
      const index = prev.findIndex((i) => i.externalId === id);
      if (index === -1) return prev;

      const item = prev[index];
      const newQty = Math.min(item?.originalQty, item?.qty + 1);

      const next = [...prev];
      next[index] = { ...item, qty: newQty };

      // reflect in removed list
      setRemovedItems((prevRemoved) => {
        const i2 = prevRemoved.findIndex((r) => r.externalId === id);
        if (i2 === -1) return prevRemoved;

        const newRemovedQty = item?.originalQty - newQty;
        if (newRemovedQty <= 0) {
          const up = [...prevRemoved];
          up.splice(i2, 1);
          return up;
        }
        const up = [...prevRemoved];
        up[i2] = { ...up[i2], qty: newQty, removedQty: newRemovedQty };
        return up;
      });

      return next;
    });
  };

  // ✅ FIXED: Calculate changed items with unique externalIds and removed quantities
  useEffect(() => {
    const changedMap = new Map();

    // Process all items to get unique entries by externalId
    const allItems = [...items, ...removedItems];

    for (const item of allItems) {
      const original = Number(item?.originalQty ?? 0);
      const current = Number(item?.qty ?? 0);
      const removed = original - current;

      // Only include items where quantity was removed
      if (removed > 0) {
        // Use Map to ensure unique externalIds
        changedMap.set(item?.externalId, {
          externalId: item?.externalId,
          quantity: removed, // ✅ Send removed quantity, not remaining
        });
      }
    }

    setChangedItems(Array.from(changedMap.values()));
  }, [items, removedItems]);

  // ✅ FIXED: Added debugging and uid fallback
  useEffect(() => {
    if (!order?.items) {
      return;
    }

    const allItems = order.items.map((it) => ({
      ...it,
      originalQty: Number(it.qty ?? 1),
    }));

    const qtyMap = allItems.reduce((acc, it) => {
      const key = it.externalId || it.id;
      acc[key] = (acc[key] || 0) + it.qty;
      return acc;
    }, {});

    const itemsWithOriginal = allItems.map((it, index) => ({
      ...it,
      uid: it.uid || it.externalId || it.id || `item-${index}`, // ✅ Add fallback uid
      originalQty: qtyMap[it.externalId || it.id] || it.qty,
    }));

    // Separate out items already removed from API
    const removed = itemsWithOriginal.filter((el) => el.taskStatus === "REMOVED");
    const active = itemsWithOriginal.filter((el) => el.taskStatus !== "REMOVED");

    setItems(active);
    setRemovedItems(removed);
  }, [order]);

  // ===== Reset from remover =====
  // const handleReset = (taskId, jobId, externalId) => {
  //   const payload = {
  //     jobId: jobId,
  //     externalId: externalId,
  //     locationId: locId,
  //     updatedBy: retailUserDetails?.uidx,
  //     updatedByName: retailUserDetails?.firstName
  //   };

  //   setRemovedItems((prev) => {
  //     const index = prev.findIndex((r) => r.taskId === taskId);
  //     if (index === -1) return prev;
  //     const item = prev[index];

  //     //  Find and update the EXISTING item in items array
  //     setItems((list) => {
  //       const existingIndex = list.findIndex((i) => i.taskId === taskId);

  //       if (existingIndex !== -1) {
  //         // Item exists, update its quantity
  //         const updated = [...list];
  //         updated[existingIndex] = {
  //           ...updated[existingIndex],
  //           qty: item.originalQty,
  //         };
  //         return updated;
  //       } else {
  //         // Item doesn't exist, add it back
  //         return [...list, { ...item, qty: item.originalQty, status: "ACCEPTED" }];
  //       }
  //     });

  //     const next = [...prev];
  //     next.splice(index, 1);
  //     return next;
  //   });
  // };

  const handleReset = (taskId, jobId, externalId) => {
    const removedItem = removedItems?.find((r) => r?.taskId === taskId);
    if (!removedItem) return;
    setResetLoader(true);
    setHasManualEdits(false);
    const payload = {
      jobId: jobId,
      externalId: externalId,
      locationId: locId,
      updatedBy: retailUserDetails?.uidx,
      updatedByName: retailUserDetails?.firstName,
    };
    revertPrevAction(payload)
      .then((res) => {
        if (res?.data?.es !== 0) {
          return Promise.reject(new Error("Invalid response"));
        }
        setRemovedItems((prev) => {
          const index = prev?.findIndex((r) => r?.taskId === taskId);
          setResetLoader(false);
          if (index === -1) return prev;
          const item = prev[index];

          //  Find and update the EXISTING item in items array
          setItems((list) => {
            const existingIndex = list?.findIndex((i) => i?.taskId === taskId);

            if (existingIndex !== -1) {
              // Item exists, update its quantity
              const updated = [...list];
              updated[existingIndex] = {
                ...updated[existingIndex],
                qty: item?.originalQty,
              };
              return updated;
            } else {
              // Item doesn't exist, add it back
              return [...list, { ...item, qty: item?.originalQty, status: "ACCEPTED" }];
            }
          });

          const next = [...prev];
          next.splice(index, 1);
          showSnackbar("Item reset successfully", "success");
          setResetLoader(false);
          return next;
        });
      })
      .catch((err) => {
        showSnackbar("Unable to reset the item", "error");
        setResetLoader(false);
      });
  };

  // ===== Complimentary search =====
  const handleSearch = (e) => setSearchValue(e.target.value);

  const handleAddComplimentary = (item) => {
    setComplimentaryItems((prev) => [
      ...prev,
      {
        name: item?.name,
        qty: 1,
        productName: item?.productName,
        externalId: item?.externalId,
        productId: item?.productId,
        variantId: item?.variantId,
        orderIndex: item?.orderIndex,
      },
    ]);
    setSearchValue("");
    setSearchResults([]);
  };

  const handleComplimentaryIncrease = (index) => {
    setComplimentaryItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], qty: updated[index].qty + 1 };
      return updated;
    });
  };

  const handleComplimentaryDecrease = (index) => {
    setComplimentaryItems((prev) => {
      const updated = [...prev];
      if (updated[index].qty > 1) {
        updated[index] = { ...updated[index], qty: updated[index].qty - 1 };
      } else {
        updated.splice(index, 1);
      }
      return updated;
    });
  };

  // ===== Toggle complimentary section =====
  const handleToggleComplimentary = () => {
    // Check if complimentary orders feature is enabled
    if (!appConfig?.enabledFeatures?.ALLOW_COMPLIMENTARY_ORDERS) {
      showSnackbar("Complimentary orders feature is disabled in POS configurations", "error");
      return;
    }
    setOpenComplimentary(!openComplimentary);
  };

  // ===== Update =====
  const handleUpdate = async (id) => {
    try {
      // Step 1: Update quantities if there are changes
      if (hasManualEdits && changedItems?.length > 0) {
        const payload = {
          jobId: id,
          taskQuantities: changedItems,
          locationId: locId,
          updatedBy: retailUserDetails.uidx || "",
          updatedByName: customerName,
        };

        const res = await editJobItemQty(payload);

        if (res?.data?.es > 0) {
          showSnackbar(res?.data?.message || "There was an error editing the order", "error");
          return;
        }
      }

      // Step 2: Add complimentary items if any
      if (complimentaryItems.length > 0) {
        const complimentaryPromises = complimentaryItems.map((item) => {
          const complimentaryPayload = {
            jobId: id,
            locationId: locId,
            title: item?.productName,
            id: item?.variantId,
            // externalId: item.externalId,
            description: `Complimentary item: ${item?.name}`,
            quantity: item?.qty,
            orderIndex: 1,
            createdBy: retailUserDetails.uidx || "",
            createdByName: customerName || "",
            metadata: {
              VariantId: item?.variantId,
              isComplimentary: true,
            },
            additionalProperties: {},
            taskAssignees: [],
          };

          return addComplimentaryinKDS(complimentaryPayload);
        });

        const complimentaryResults = await Promise.all(complimentaryPromises);

        // Check if any complimentary item failed
        const failedComplimentary = complimentaryResults.some((res) => res?.data?.es > 0);

        if (failedComplimentary) {
          showSnackbar("Some complimentary items could not be added", "warning");
        }
      }

      // Step 3: Show success message and close modal
      if (!(!hasManualEdits && changedItems?.length === 0 && complimentaryItems?.length === 0)) {
        const successMessage =
          changedItems.length > 0 && complimentaryItems.length > 0
            ? "Order updated and complimentary items added successfully"
            : changedItems.length > 0
            ? "The order was successfully edited"
            : "Complimentary items added successfully";
        showSnackbar(successMessage, "success");
      }

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      showSnackbar(err.message || "There was an error updating the order", "error");
    }
  };

  const handleItemSelect = (taskId, checked) => {
    setSelectedItems((prev) => {
      if (checked) {
        return [...prev, taskId];
      } else {
        return prev.filter((id) => id !== taskId);
      }
    });
  };

  const eligibleCancellationItems = cancellableItems(order);
  const selectedCancellationItems = eligibleCancellationItems.filter((item) =>
    selectedItems.includes(item?.taskId)
  );
  const allCancellationItemsSelected =
    eligibleCancellationItems.length > 0 &&
    selectedCancellationItems.length === eligibleCancellationItems.length;

  const fetchCancellationContext = async () => {
    setIsLoadingCancellationContext(true);
    setCancellationContextError("");
    setCancellationContext(null);
    try {
      const context = await loadCancellationContext(order, dataRetriveWhenPageRefresh);
      setCancellationContext(context);
      setMarkWastage(context?.cartId ? null : false);
      setDoNotCharge(false);
    } catch (error) {
      setCancellationContextError(
        error?.message || "Unable to verify the linked cart. Retry before cancelling."
      );
    } finally {
      setIsLoadingCancellationContext(false);
    }
  };

  const handleOpenCancellationReview = () => {
    if (!appConfig?.enabledFeatures?.REJECT_ORDERS) {
      showSnackbar("Reject orders feature is disabled in POS configurations", "error");
      return;
    }
    if (selectedCancellationItems.length === 0) return;
    setShowCancellationReview(true);
    setCancellationReason("");
    setCustomCancellationReason("");
    setCancellationError("");
    fetchCancellationContext();
  };

  const handleCancelSelectedItems = async () => {
    if (cancellingRef.current || isLoadingCancellationContext || !cancellationContext) return;
    if (!appConfig?.enabledFeatures?.REJECT_ORDERS) {
      showSnackbar("Reject orders feature is disabled in POS configurations", "error");
      return;
    }

    const selectedTaskIds = selectedCancellationItems.map((item) => item.taskId);
    if (selectedTaskIds.length === 0) {
      setCancellationError("Select at least one current item to cancel.");
      return;
    }

    const validation = validateRejectionReason(cancellationReason, customCancellationReason);
    if (validation.error) {
      setCancellationError(
        cancellationReason === "OTHER" && customCancellationReason.trim().length > 500
          ? "Keep the custom cancellation reason within 500 characters."
          : cancellationReason === "OTHER"
          ? "Enter a custom cancellation reason with at least 3 characters."
          : "Choose a cancellation reason before continuing."
      );
      return;
    }
    if (markWastage === null) {
      setCancellationError("Choose whether these items should be marked as wastage.");
      return;
    }
    if (doNotCharge && !cancellationContext.unpaid) {
      setCancellationError("The cart is not confirmed unpaid. Billing changes were not processed.");
      return;
    }

    cancellingRef.current = true;
    setIsCancelling(true);
    setCancellationError("");
    // Cancel the full (maximum) quantity of each selected item, grouped by externalId
    const cancelQtyMap = new Map();
    for (const item of selectedCancellationItems) {
      cancelQtyMap.set(item?.externalId, (cancelQtyMap.get(item?.externalId) || 0) + Number(item?.qty));
    }
    const taskQuantities = Array.from(cancelQtyMap, ([externalId, quantity]) => ({ externalId, quantity }));
    try {
      const res = await editJobItemQty({
        jobId: order?.id,
        taskQuantities,
        locationId: locId,
        updatedBy: retailUserDetails.uidx || "",
        updatedByName: customerName,
      });
      if (res?.data?.es > 0) {
        throw new Error(res?.data?.message || "Unable to cancel the selected items.");
      }
      showSnackbar(
        selectedTaskIds.length === eligibleCancellationItems.length
          ? "Order cancelled successfully"
          : "Items cancelled successfully",
        "success"
      );
      try {
        if (onSuccess) await onSuccess();
      } catch {
        showSnackbar("Cancellation succeeded, but the order list could not be refreshed.", "warning");
      }
      onClose();
    } catch (error) {
      if (error?.partialCancellation) {
        try {
          if (onSuccess) await onSuccess();
        } catch {
          // Close after a partial result to avoid retrying items already cancelled.
        }
        showSnackbar(error.message, "error");
        onClose();
      } else {
        setCancellationError(error?.message || "Unable to cancel the selected items.");
        showSnackbar(error?.message || "Unable to cancel the selected items.", "error");
      }
    } finally {
      cancellingRef.current = false;
      setIsCancelling(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => {
        if (!cancellingRef.current) onClose();
      }}
      ModalProps={{
        container: () =>
          typeof document !== "undefined"
            ? document.fullscreenElement || document.body
            : undefined,
      }}
      PaperProps={{
        className: "edit-drawer-paper",
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "edit-order-title",
      }}
    >
          <div className="edit-modal">
            {showCancellationReview ? (
              <CancellationReview
                selectedItems={selectedCancellationItems}
                eligibleItems={eligibleCancellationItems}
                context={cancellationContext}
                contextError={cancellationContextError}
                isLoadingContext={isLoadingCancellationContext}
                isCancelling={isCancelling}
                reason={cancellationReason}
                customReason={customCancellationReason}
                error={cancellationError}
                markWastage={markWastage}
                doNotCharge={doNotCharge}
                onClose={() => {
                  if (!cancellingRef.current) onClose();
                }}
                onBack={() => {
                  if (!cancellingRef.current) {
                    setShowCancellationReview(false);
                    setCancellationError("");
                  }
                }}
                onConfirm={handleCancelSelectedItems}
                onRetry={fetchCancellationContext}
                onReasonChange={(value) => {
                  setCancellationReason(value);
                  setCancellationError("");
                }}
                onCustomReasonChange={(value) => {
                  setCustomCancellationReason(value);
                  setCancellationError("");
                }}
                onWastageChange={(value) => {
                  setMarkWastage(value);
                  setCancellationError("");
                }}
                onDoNotChargeChange={(value) => {
                  setDoNotCharge(value);
                  setCancellationError("");
                }}
              />
            ) : (
              <>
            <div className="edit-modal-header">
              <h2 className="edit-title" id="edit-order-title">Edit Order</h2>
              <p className="sr-only">
                Edit item selection, quantities, and complimentary items for this KDS order.
              </p>
              <button type="button" className="close-btn" onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>

            <div className="edit-drawer-body">
              <div className="edit-content">
              <div className="edit-selection-toolbar">
                <label>
                  <input
                    type="checkbox"
                    aria-label="Select all cancellable items"
                    checked={allCancellationItemsSelected}
                    disabled={eligibleCancellationItems.length === 0 || isCancelling}
                    onChange={(event) => {
                      const eligibleIds = eligibleCancellationItems
                        .map((item) => item?.taskId)
                        .filter(Boolean);
                      setSelectedItems((previous) => {
                        if (event.target.checked) {
                          return [...new Set([...previous, ...eligibleIds])];
                        }
                        return previous.filter((id) => !eligibleIds.includes(id));
                      });
                    }}
                  />
                  <span>Select all cancellable items</span>
                </label>
                <span>{eligibleCancellationItems.length} eligible</span>
              </div>
              {/* ==== Items List ==== */}
              <div className="edit-items-list">
                {items.length === 0 && (
                  <p style={{ padding: "20px", textAlign: "center", color: "#999" }}>
                    No items to display
                  </p>
                )}
                {items?.map((item) => (
                  <div key={item?.uid} className="edit-item-container">
                    <div className="edit-item-row">
                      <div className="edit-item-line">
                        <button
                          type="button"
                          className="custom-checkbox"
                          role="checkbox"
                          aria-checked={selectedItems?.includes(item?.taskId)}
                          onClick={() =>
                            handleItemSelect(item?.taskId, !selectedItems?.includes(item?.taskId))
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </button>

                        <div className="edit-item-label-container">
                          {/* <p className="edit-item-name"
                            {item?.qty !== item?.originalQty && (
                              <span className="original-qty">x{item?.originalQty}</span>
                            )}
                          </p> */}
                          <div className="edit-item-name-container">
                            <p className="edit-item-name">{item?.name}</p>
                            {item?.qty !== item?.originalQty && (
                              <span className="original-qty">x{item?.originalQty}</span>
                            )}
                          </div>
                          {item?.metadata?.isComplimentary && (
                            <p className="edit-item-name-complimentary">Complimentary</p>
                          )}
                        </div>
                      </div>
                      <div className="edit-item-actions">
                        <button
                          onClick={() => handleDecrease(item?.externalId)}
                          className="qty-btn"
                          disabled={!selectedItems?.includes(item?.taskId)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M5 12h14"></path>
                          </svg>
                        </button>
                        <span className="qty-display">{item?.qty}</span>
                        <button
                          className="qty-btn"
                          onClick={() => handleIncrease(item?.externalId)}
                          disabled={item?.originalQty === item?.qty}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M5 12h14"></path>
                            <path d="M12 5v14"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            {/* ==== Remover Section ==== */}
            {removedItems.length > 0 && (
              <div className="remover-section">
                <div className="removed-item-title-logo">
                  <TrashIcon width={16} height={16} style={{ color: "red" }} />
                  <p className="remover-title">Removed</p>
                </div>
                {removedItems.map((item) => (
                  <div key={item?.uid} className="removed-item-edit">
                    <div className="removed-info">
                      {item?.metadata?.isComplimentary && <GiftIcon className="edit-gift-icon" />}
                      <span>{item?.name}</span> × {item?.removedQty}
                    </div>
                    <button
                      className="reset-link"
                      onClick={() => handleReset(item?.taskId ?? "", item?.jobId, item?.externalId)}
                    >
                      {resetLoader ? (
                        <>
                          <CircularProgress size={20} sx={{ color: "var(--blue-b0)" }} />
                        </>
                      ) : (
                        <>
                          Reset
                          <ArrowPathRoundedSquareIcon
                            height={20}
                            width={20}
                            style={{ color: "#0860E6" }}
                          />
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ==== Complimentary Section ==== */}
            {openComplimentary && (
              <div className="complimentary-section">
                <div>
                  <label className="complimentary-label">Add Complimentary</label>
                  <div className="complimentary-search-wrapper">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="complimentary-search-icon"
                      aria-hidden="true"
                    >
                      <path d="m21 21-4.34-4.34"></path>
                      <circle cx="11" cy="11" r="8"></circle>
                    </svg>
                    <input
                      className="complimentary-search-input"
                      type="text"
                      placeholder="Search items"
                      data-testid="input-kds-complimentary-search"
                      value={searchValue}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                {isSearching && <div className="search-loading">Loading...</div>}
                {searchValue && !isSearching && searchResults.length > 0 && (
                  <div className="search-dropdown">
                    {searchResults?.map((item, idx) => (
                      <button
                        type="button"
                        key={idx}
                        className="search-dropdown-item"
                        data-testid={`button-kds-add-complimentary-${item?.name
                          ?.toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        onClick={() => handleAddComplimentary(item)}
                        disabled={!item?.inStock}
                      >
                        <span className="search-result-name">{item?.name}</span>
                        {!item?.inStock ? (
                          <span className="out-of-stock-badge">Out of Stock</span>
                        ) : (
                          <span className="search-result-add">Add</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {searchValue && !isSearching && searchResults.length === 0 && (
                  <div className="search-no-results">No items found</div>
                )}

                {complimentaryItems.length > 0 && (
                  <div className="complimentary-items-list">
                    {complimentaryItems.map((c, i) => (
                      <div key={i} className="complimentary-item">
                        <span className="complimentary-item-name">{c.name}</span>
                        <div className="complimentary-item-actions">
                          <button
                            className="qty-btn-small"
                            onClick={() => handleComplimentaryDecrease(i)}
                          >
                            −
                          </button>
                          <span className="qty-display-small">{c.qty}</span>
                          <button
                            className="qty-btn-small"
                            onClick={() => handleComplimentaryIncrease(i)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>

            {/* ==== Footer ==== */}
            <div className="edit-footer">
              <div className="edit-footer-content">
                <button
                  type="button"
                  className="cancellation-entry-btn"
                  onClick={handleOpenCancellationReview}
                  disabled={selectedCancellationItems.length === 0 || isCancelling}
                >
                  {selectedCancellationItems.length === eligibleCancellationItems.length &&
                  eligibleCancellationItems.length > 0
                    ? "Cancel order"
                    : selectedCancellationItems.length === 1
                    ? "Cancel item"
                    : "Cancel items"}
                </button>
                <div className="edit-footer-right">
                  <button className="update-btn" onClick={handleToggleComplimentary}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12h14"></path>
                      <path d="M12 5v14"></path>
                    </svg>
                    {openComplimentary ? "Close Complimentary" : "Add Complimentary"}
                  </button>
                  <button className="update-btn" onClick={() => handleUpdate(order?.id)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                      <path d="M3 3v5h5"></path>
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                      <path d="M16 16h5v5"></path>
                    </svg>
                    Update Changes
                  </button>
                </div>
              </div>
            </div>
              </>
            )}
          </div>
    </Drawer>
  );
};

export default EditItem;
